"use client"

import { useRef, useEffect, useState } from "react"
import type { Enemy } from "@/types/enemy.types"
import type { Tower } from "@/types/tower.types"
import type { Hero } from "@/types/hero.types"
import type { LevelConfig, Position, ViewportState } from "@/types/game.types"
import type { SummonedUnit } from "@/types/summon.types"
import type { HeroInventory } from "@/types/item.types"
import type { BarracksSoldier } from "@/types/barracks.types"

import { PortraitSection } from "./game-ui/bottom-bar/portrait-section"
import { HeroName } from "./game-ui/bottom-bar/hero-name"
import { HealthBar } from "./game-ui/bottom-bar/health-bar"
import { ForcefieldBar } from "./game-ui/bottom-bar/forcefield-bar"
import { ManaBar } from "./game-ui/bottom-bar/mana-bar"
import { XpBar } from "./game-ui/bottom-bar/xp-bar"
import { DamageBar } from "./game-ui/bottom-bar/damage-bar"
import { ArmorBar } from "./game-ui/bottom-bar/armor-bar"
import { SkillsSection } from "./game-ui/bottom-bar/skills-section"
import { InventorySection } from "./game-ui/bottom-bar/inventory-section"
import { MinimapSection } from "./game-ui/bottom-bar/minimap-section"
import { EnemyPortraitSection } from "./game-ui/bottom-bar/enemy-portrait-section"
import { SummonPortraitSection } from "./game-ui/bottom-bar/summon-portrait-section"
import { SoldierPortraitSection } from "./game-ui/bottom-bar/soldier-portrait-section"
import { TowerPortraitSection } from "./game-ui/bottom-bar/tower-portrait-section"
import { DroppodPortraitSection } from "./game-ui/bottom-bar/droppod-portrait-section"
import { DroppodStatusText } from "./game-ui/bottom-bar/droppod-status-text"
import { DroppodSystemsText } from "./game-ui/bottom-bar/droppod-systems-text"
import { MobileStatsPanel } from "./game-ui/bottom-bar/mobile-stats-panel"
import { MobileSkillsPanel } from "./game-ui/bottom-bar/mobile-skills-panel"
import { MobileInventoryPanel } from "./game-ui/bottom-bar/mobile-inventory-panel"
import { enemyTypes } from "@/data/enemy-types"
import { towerTypes, getTowerClassName } from "@/data/tower-types"
import { summonConfigs } from "@/types/summon.types"
import { getEquipmentArmorBonus, getEquipmentDamageBonus, getEquipmentMaxHealthBonus } from "@/systems/item-system"
import { getFootmanConfig } from "@/data/barracks-config"
import type { EnemyTypeConfig, EnemyVariant } from "@/types/enemy.types"

// Get the appropriate display name for an enemy based on its variant
function getEnemyDisplayName(
  enemyConfig: EnemyTypeConfig,
  variant: EnemyVariant
): string {
  if (variant === "unique" && enemyConfig.variants?.unique?.displayName) {
    return enemyConfig.variants.unique.displayName
  }
  if (variant === "elite" && enemyConfig.variants?.elite?.displayName) {
    return enemyConfig.variants.elite.displayName
  }
  return enemyConfig.displayName
}

// Background image dimensions
const BG_WIDTH = 512
const BG_HEIGHT = 152

interface GameBottomBarProps {
  stats: {
    money: number
    lives: number
    wave: number
    score: number
  }
  heroTypes: typeof import("@/data/hero-types").heroTypes
  selectedHeroType: string
  hero: Hero | null
  enemies: Enemy[]
  towers: Tower[]
  summonedUnits?: SummonedUnit[]
  level: LevelConfig
  viewport: ViewportState
  isHeroSelected?: boolean
  selectedEnemyId?: string | null
  selectedSummonId?: string | null
  selectedSoldierId?: string | null
  selectedTowerId?: string | null
  barracksSoldiers?: BarracksSoldier[]
  isDroppodSelected?: boolean
  cloneCount?: number
  showCloneParticles?: boolean
  onViewportChange: (position: Position) => void
  onZoomIn: () => void
  onZoomOut: () => void
  isZoomedOut: boolean
  freePremiumTowers: number
  onSelectHero?: () => void
  onDeselectHero?: () => void
  onDeselectEnemy?: () => void
  onDeselectSummon?: () => void
  onDeselectSoldier?: () => void
  onDeselectTower?: () => void
  onUseSkill?: (skillId: string) => void
  onUpgradeSkill?: (skillId: string) => void
  onMobileSkillActivated?: () => void // Called when a targeted skill is activated from mobile panel
  isDesktop?: boolean
  pendingSkillId?: string | null
  heroInventory?: HeroInventory
  onUseItem?: (slotIndex: number) => void
  onDropItem?: (slotIndex: number) => void
  pendingItemSlot?: number | null
  lastUpgradedSkillId?: string | null
  onItemDragStart?: (slotIndex: number, itemDefId: string) => void
  onItemDragEnd?: () => void
  pillarWidth?: number
  pillarboxedWidth?: number // Original pillarboxed width to constrain bottom bar size
  showBeaconParticles?: boolean
  cloneToast?: string | null
  /** Styling for cloneToast - errors must not read as the blue success note */
  toastTone?: "info" | "error"
}

export const GameBottomBar = ({
  heroTypes,
  selectedHeroType,
  hero,
  enemies,
  towers,
  summonedUnits = [],
  level,
  viewport,
  isHeroSelected = false,
  selectedEnemyId,
  selectedSummonId,
  selectedSoldierId,
  selectedTowerId,
  barracksSoldiers = [],
  isDroppodSelected = false,
  cloneCount = 0,
  showCloneParticles = false,
  onViewportChange,
  onZoomIn,
  onZoomOut,
  isZoomedOut,
  onSelectHero,
  onDeselectHero,
  onDeselectEnemy,
  onDeselectSummon,
  onDeselectSoldier,
  onDeselectTower,
  onUseSkill,
  onMobileSkillActivated,
  isDesktop = false,
  pendingSkillId,
  heroInventory,
  onUseItem,
  onDropItem,
  pendingItemSlot,
  lastUpgradedSkillId,
  onItemDragStart,
  onItemDragEnd,
  pillarWidth = 0,
  pillarboxedWidth,
  showBeaconParticles = false,
  cloneToast = null,
  toastTone = "info",
}: GameBottomBarProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [barWidth, setBarWidth] = useState(0)
  const [showMobileStatsPanel, setShowMobileStatsPanel] = useState(false)
  const [showMobileSkillsPanel, setShowMobileSkillsPanel] = useState(false)
  const [showMobileInventoryPanel, setShowMobileInventoryPanel] = useState(false)

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setBarWidth(containerRef.current.offsetWidth)
      }
    }
    updateWidth()
    window.addEventListener("resize", updateWidth)
    return () => window.removeEventListener("resize", updateWidth)
  }, [])

  // Calculate scale based on container width relative to background image
  const scale = barWidth / BG_WIDTH
  const bgHeight = BG_HEIGHT * scale

  // Find selected enemy
  const selectedEnemy = selectedEnemyId ? enemies.find((e) => e.id === selectedEnemyId) : null
  const selectedEnemyConfig = selectedEnemy ? enemyTypes[selectedEnemy.type] : null

  // Find selected summon
  const selectedSummon = selectedSummonId ? summonedUnits.find((s) => s.id === selectedSummonId) : null
  const selectedSummonConfig = selectedSummon ? summonConfigs[selectedSummon.type] : null

  // Find selected soldier and its barracks tower
  const selectedSoldier = selectedSoldierId ? barracksSoldiers.find((s) => s.id === selectedSoldierId) : null
  const soldierBarracksTower = selectedSoldier ? towers.find((t) => t.id === selectedSoldier.barracksTowerId) : null
  const soldierConfig = soldierBarracksTower ? getFootmanConfig(soldierBarracksTower.level) : null

  // Find selected tower
  const selectedTower = selectedTowerId ? towers.find((t) => t.id === selectedTowerId) : null
  const selectedTowerConfig = selectedTower ? towerTypes[selectedTower.type] : null

  // Get hero config for type name
  const heroConfig = hero ? heroTypes[hero.type] : null
  // Get equipment bonuses from inventory
  const equipmentArmorBonus = heroInventory ? getEquipmentArmorBonus(heroInventory) : 0
  const equipmentDamageBonus = heroInventory ? getEquipmentDamageBonus(heroInventory) : 0
  const equipmentMaxHealthBonus = heroInventory ? getEquipmentMaxHealthBonus(heroInventory) : 0
  // Use actual hero stats plus equipment bonuses
  const heroArmor = (hero?.armor ?? 0) + equipmentArmorBonus
  const heroMaxHealth = (hero?.maxHealth ?? 1) + equipmentMaxHealthBonus

  // Determine what to show (soldier > summon > tower > enemy priority)
  const showingSoldier = !!selectedSoldier && !!soldierConfig && !!soldierBarracksTower
  const showingSummon = !showingSoldier && !!selectedSummon && !!selectedSummonConfig
  const showingTower = !showingSoldier && !showingSummon && !!selectedTower && !!selectedTowerConfig
  const showingEnemy = !showingSoldier && !showingSummon && !showingTower && !!selectedEnemy && !!selectedEnemyConfig

  return (
    <div
      ref={containerRef}
      id="game-bottom-bar"
      className={`${isDesktop ? "absolute" : "fixed"} bottom-0 ${isDesktop && pillarboxedWidth ? "left-1/2 -translate-x-1/2" : "left-0 right-0"} z-40 flex-shrink-0`}
      style={{
        height: bgHeight || BG_HEIGHT,
        paddingBottom: isDesktop ? "0" : "env(safe-area-inset-bottom)",
        width: isDesktop && pillarboxedWidth ? pillarboxedWidth : undefined,
      }}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={isDroppodSelected ? "/images/bottom-bar-droppod-bg.png" : "/images/bottom-bar-bg.png"}
          alt="Bottom bar background"
          width={BG_WIDTH}
          height={BG_HEIGHT}
          className="w-full h-full"
          style={{ imageRendering: "pixelated" }}
        />
      </div>

      {/* Show soldier/summon/enemy/droppod/hero stats based on selection */}
      {showingSoldier && selectedSoldier && soldierConfig && soldierBarracksTower ? (
        <>
          {/* Soldier portrait - shows barracks level portrait (what soldier will become on respawn) */}
          <SoldierPortraitSection
            soldier={selectedSoldier}
            barracksTowerLevel={soldierBarracksTower.level}
            onDeselectSoldier={onDeselectSoldier}
            scale={scale}
          />

          {/* Soldier name - shows randomly assigned medieval fantasy name */}
          <HeroName name={selectedSoldier.name} scale={scale} />

          {/* Soldier health bar */}
          <HealthBar current={selectedSoldier.health} max={selectedSoldier.maxHealth} scale={scale} />

          {/* Soldier mana bar (soldiers don't have mana) */}
          <ManaBar current={0} max={1} scale={scale} />

          {/* Soldier level and type - shows barracks level and class (what soldier will become on respawn) */}
          <XpBar
            current={0}
            toNextLevel={1}
            level={soldierBarracksTower.level}
            heroType={getFootmanConfig(soldierBarracksTower.level).name}
            scale={scale}
          />

          {/* On mobile, clicking damage/armor area opens stats panel */}
          {!isDesktop && (
            <button
              onClick={() => setShowMobileStatsPanel(true)}
              className="absolute cursor-pointer"
              style={{
                left: 118 * scale,
                top: 95 * scale,
                width: 130 * scale,
                height: 40 * scale,
                background: "transparent",
                border: "none",
                zIndex: 10,
              }}
              aria-label="View damage and armor type details"
            />
          )}

          {/* Soldier damage bar */}
          <DamageBar min={selectedSoldier.damage.min} max={selectedSoldier.damage.max} scale={scale} damageType="normal" />

          {/* Soldier armor bar */}
          <ArmorBar armor={selectedSoldier.armor} scale={scale} armorType="medium" />

          {/* Empty skills section for soldier */}
          <SkillsSection scale={scale} hero={null} onUseSkill={undefined} />

          {/* Inventory section */}
          <InventorySection scale={scale} inventory={heroInventory} onUseItem={onUseItem} pendingItemSlot={pendingItemSlot} onDragStart={onItemDragStart} onDragEnd={onItemDragEnd} showBeaconParticles={showBeaconParticles} />
        </>
      ) : showingSummon ? (
        <>
          {/* Summon portrait */}
          <SummonPortraitSection
            summon={selectedSummon}
            onDeselectSummon={onDeselectSummon}
            scale={scale}
          />

          {/* Summon name - use custom name if set, otherwise config displayName */}
          <HeroName name={selectedSummon.name || selectedSummonConfig.displayName} scale={scale} />

          {/* Summon health bar */}
          <HealthBar current={selectedSummon.health} max={selectedSummon.maxHealth} scale={scale} />

          {/* Summon mana bar (always 0 for summons) */}
          <ManaBar current={0} max={1} scale={scale} />

          {/* Summon level (shown in XP bar position) */}
          <XpBar
            current={0}
            toNextLevel={1}
            level={selectedSummon.level}
            heroType={selectedSummon.type === "fox" ? "Fox" : selectedSummon.type === "boar" ? "Boar" : selectedSummon.type === "siege-tower" ? "Turret" : "Wolf"}
            scale={scale}
          />

          {/* On mobile, clicking damage/armor area opens stats panel */}
          {!isDesktop && (
            <button
              onClick={() => setShowMobileStatsPanel(true)}
              className="absolute cursor-pointer"
              style={{
                left: 118 * scale,
                top: 95 * scale,
                width: 130 * scale,
                height: 40 * scale,
                background: "transparent",
                border: "none",
                zIndex: 10,
              }}
              aria-label="View damage and armor type details"
            />
          )}

          {/* Summon damage bar */}
          <DamageBar min={selectedSummon.damage.min} max={selectedSummon.damage.max} scale={scale} damageType={selectedSummon.damageType} />

          {/* Summon armor bar */}
          <ArmorBar armor={selectedSummon.armor} scale={scale} armorType={selectedSummon.armorType} />

          {/* Empty skills section for summon */}
          <SkillsSection scale={scale} hero={null} onUseSkill={undefined} />

          {/* Inventory section */}
          <InventorySection scale={scale} inventory={heroInventory} onUseItem={onUseItem} pendingItemSlot={pendingItemSlot} onDragStart={onItemDragStart} onDragEnd={onItemDragEnd} showBeaconParticles={showBeaconParticles} />
        </>
      ) : isDroppodSelected ? (
        <>
          {/* Droppod portrait */}
          <DroppodPortraitSection
            scale={scale}
            hasClone={cloneCount > 0}
            showCloneParticles={showCloneParticles}
          />

          {/* Droppod status text - repositioned with shadow */}
          <DroppodStatusText hasClone={cloneCount > 0} scale={scale} />

          {/* ALL SYSTEMS GO text - repositioned with shadow */}
          <DroppodSystemsText scale={scale} />

          {/* Hero forcefield bar (for Rift Blade) */}
          <ForcefieldBar current={hero?.forcefield || 0} max={hero?.maxForcefield || 0} scale={scale} />

          {/* Hero health bar */}
          <HealthBar current={hero?.health || 0} max={heroMaxHealth} scale={scale} />

          {/* Hero mana bar */}
          <ManaBar current={hero?.mana || 0} max={hero?.maxMana || 1} scale={scale} />

          {/* Hero skills - functional with click handlers */}
          <SkillsSection
            scale={scale}
            hero={hero}
            onUseSkill={onUseSkill}
            pendingSkillId={pendingSkillId}
            lastUpgradedSkillId={lastUpgradedSkillId}
            disableClicks={!isDesktop}
          />

          {/* On mobile, clicking skills area opens skills panel */}
          {!isDesktop && hero && (
            <button
              onClick={() => setShowMobileSkillsPanel(true)}
              className="absolute cursor-pointer"
              style={{
                // Slightly larger than the drawn 65px grid - keeps the tap
                // target comfortably above the 44px minimum on small phones
                left: 248 * scale,
                top: 57 * scale,
                width: 77 * scale,
                height: 77 * scale,
                background: "transparent",
                border: "none",
                zIndex: 10,
              }}
              aria-label="Open skills panel"
            />
          )}

          {/* Inventory section */}
          <InventorySection
            scale={scale}
            inventory={heroInventory}
            onUseItem={onUseItem}
            pendingItemSlot={pendingItemSlot}
            onDragStart={onItemDragStart}
            onDragEnd={onItemDragEnd}
            disableClicks={!isDesktop}
            showBeaconParticles={showBeaconParticles}
          />

          {/* On mobile, clicking inventory area opens inventory panel */}
          {!isDesktop && hero && (
            <button
              onClick={() => setShowMobileInventoryPanel(true)}
              className="absolute cursor-pointer"
              style={{
                // Padded beyond the drawn 74x49 slot grid so the tap target
                // clears the 44px minimum height on small phones
                left: 327 * scale,
                top: 70 * scale,
                width: 86 * scale,
                height: 69 * scale,
                background: "transparent",
                border: "none",
                zIndex: 10,
              }}
              aria-label="Open inventory panel"
            />
          )}
        </>
      ) : showingTower && selectedTower && selectedTowerConfig ? (
        <>
          {/* Tower portrait */}
          <TowerPortraitSection
            tower={selectedTower}
            onDeselectTower={onDeselectTower}
            scale={scale}
          />

          {/* Tower name - show unit name for archer/mage/siege, tower type for shrine, or full name for others */}
          <HeroName
            name={
              (selectedTower.type === "archer" || selectedTower.type === "mage" || selectedTower.type === "siege") && selectedTower.name
                ? selectedTower.name
                : selectedTowerConfig.name
            }
            scale={scale}
          />

          {/* Tower health bar */}
          <HealthBar current={selectedTower.health} max={selectedTower.maxHealth} scale={scale} />

          {/* Tower mana bar (towers don't have mana, show empty) */}
          <ManaBar current={0} max={1} scale={scale} />

          {/* Tower level and class (shown in XP bar position) */}
          <XpBar
            current={0}
            toNextLevel={1}
            level={selectedTower.level}
            heroType={
              (selectedTower.type === "archer" || selectedTower.type === "mage" || selectedTower.type === "siege")
                ? getTowerClassName(selectedTower.type, selectedTower.level)
                : selectedTower.type === "shrine"
                  ? "Shrine"
                  : selectedTowerConfig.name
            }
            scale={scale}
          />

          {/* Tower damage bar */}
          <DamageBar min={selectedTower.damage} max={selectedTower.damage} scale={scale} damageType={selectedTower.attackType} />

          {/* Tower armor bar (towers don't have armor) */}
          <ArmorBar armor={0} scale={scale} />

          {/* Empty skills section for tower */}
          <SkillsSection scale={scale} hero={null} onUseSkill={undefined} />

          {/* Inventory section */}
          <InventorySection scale={scale} inventory={heroInventory} onUseItem={onUseItem} pendingItemSlot={pendingItemSlot} onDragStart={onItemDragStart} onDragEnd={onItemDragEnd} showBeaconParticles={showBeaconParticles} />
        </>
      ) : showingEnemy ? (
        <>
          {/* Enemy portrait */}
          <EnemyPortraitSection
            enemyConfig={selectedEnemyConfig}
            enemy={selectedEnemy}
            onDeselectEnemy={onDeselectEnemy}
            scale={scale}
          />

          {/* Enemy name - shows variant-specific name (e.g., "Rat King" for unique) */}
          <HeroName name={getEnemyDisplayName(selectedEnemyConfig, selectedEnemy.variant)} scale={scale} />

          {/* Enemy health bar */}
          <HealthBar current={selectedEnemy.health} max={selectedEnemy.maxHealth} scale={scale} />

          {/* Enemy mana bar (enemies don't have mana, show empty) */}
          <ManaBar current={0} max={1} scale={scale} />

          {/* Enemy level and type (shown in XP bar position) - includes progression bonus */}
          <XpBar
            current={0}
            toNextLevel={1}
            level={selectedEnemyConfig.level + (selectedEnemy?.progressionLevel ?? 0)}
            heroType={selectedEnemyConfig.name}
            scale={scale}
          />

          {/* On mobile, clicking damage/armor area opens stats panel */}
          {!isDesktop && (
            <button
              onClick={() => setShowMobileStatsPanel(true)}
              className="absolute cursor-pointer"
              style={{
                left: 118 * scale,
                top: 95 * scale,
                width: 130 * scale,
                height: 40 * scale,
                background: "transparent",
                border: "none",
                zIndex: 10,
              }}
              aria-label="View damage and armor type details"
            />
          )}

          {/* Enemy damage bar (same position as hero) */}
          <DamageBar min={selectedEnemy.damage.min} max={selectedEnemy.damage.max} scale={scale} damageType={selectedEnemy.damageType} />

          {/* Enemy armor bar (same position as hero) */}
          <ArmorBar armor={selectedEnemy.armor} scale={scale} armorType={selectedEnemy.defenseType} />

          {/* Empty skills section for enemy */}
          <SkillsSection scale={scale} hero={null} onUseSkill={undefined} />

          {/* Inventory section */}
          <InventorySection scale={scale} inventory={heroInventory} onUseItem={onUseItem} pendingItemSlot={pendingItemSlot} onDragStart={onItemDragStart} onDragEnd={onItemDragEnd} showBeaconParticles={showBeaconParticles} />
        </>
      ) : (
        <>
          {/* Hero portrait - show white border when selected */}
          <PortraitSection
            heroConfig={heroConfig}
            heroType={hero?.type}
            heroColor={hero?.color}
            selectedHeroType={selectedHeroType}
            onSelectHero={onSelectHero}
            scale={scale}
            isHeroSelected={isHeroSelected}
          />

          <HeroName name={heroConfig?.displayName || heroConfig?.name || "Hero"} scale={scale} />

          <ForcefieldBar current={hero?.forcefield || 0} max={hero?.maxForcefield || 0} scale={scale} />

          <HealthBar current={hero?.health || 0} max={heroMaxHealth} scale={scale} />

          <ManaBar current={hero?.mana || 0} max={hero?.maxMana || 1} scale={scale} />

          <XpBar
            current={hero?.experience || 0}
            toNextLevel={hero?.experienceToNextLevel || 1}
            level={hero?.level || 1}
            heroType={heroConfig?.name || "Hero"}
            scale={scale}
          />

          {/* On mobile, clicking damage/armor area opens stats panel */}
          {!isDesktop && (
            <button
              onClick={() => setShowMobileStatsPanel(true)}
              className="absolute cursor-pointer"
              style={{
                left: 118 * scale,
                top: 95 * scale,
                width: 130 * scale,
                height: 40 * scale,
                background: "transparent",
                border: "none",
                zIndex: 10,
              }}
              aria-label="View damage and armor type details"
            />
          )}

          <DamageBar min={hero?.damage.min ?? 0} max={hero?.damage.max ?? 0} scale={scale} bonus={equipmentDamageBonus} damageType={hero?.damageType} />

          <ArmorBar armor={hero?.armor ?? 0} scale={scale} bonus={equipmentArmorBonus} armorType={hero?.armorType} />

          <SkillsSection scale={scale} hero={hero} onUseSkill={onUseSkill} pendingSkillId={pendingSkillId} lastUpgradedSkillId={lastUpgradedSkillId} disableClicks={!isDesktop} />

          {/* On mobile, clicking skills area opens skills panel */}
          {!isDesktop && hero && (
            <button
              onClick={() => setShowMobileSkillsPanel(true)}
              className="absolute cursor-pointer"
              style={{
                // Slightly larger than the drawn 65px grid - keeps the tap
                // target comfortably above the 44px minimum on small phones
                left: 248 * scale,
                top: 57 * scale,
                width: 77 * scale,
                height: 77 * scale,
                background: "transparent",
                border: "none",
                zIndex: 10,
              }}
              aria-label="Open skills panel"
            />
          )}

          <InventorySection scale={scale} inventory={heroInventory} onUseItem={onUseItem} pendingItemSlot={pendingItemSlot} onDragStart={onItemDragStart} onDragEnd={onItemDragEnd} disableClicks={!isDesktop} showBeaconParticles={showBeaconParticles} />

          {/* On mobile, clicking inventory area opens inventory panel */}
          {!isDesktop && hero && (
            <button
              onClick={() => setShowMobileInventoryPanel(true)}
              className="absolute cursor-pointer"
              style={{
                // Padded beyond the drawn 74x49 slot grid so the tap target
                // clears the 44px minimum height on small phones
                left: 327 * scale,
                top: 70 * scale,
                width: 86 * scale,
                height: 69 * scale,
                background: "transparent",
                border: "none",
                zIndex: 10,
              }}
              aria-label="Open inventory panel"
            />
          )}
        </>
      )}

      {/* Minimap is always shown */}
      <MinimapSection
        enemies={enemies}
        towers={towers}
        hero={hero}
        level={level}
        viewport={viewport}
        onViewportChange={onViewportChange}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        isZoomedOut={isZoomedOut}
        onDeselectHero={onDeselectHero}
        scale={scale}
        isDroppodSelected={isDroppodSelected}
      />

      {/* Mobile Stats Panel - shows damage/armor type details */}
      {(() => {
        // Determine which unit's stats to show
        if (showingSoldier && selectedSoldier && soldierConfig) {
          return (
            <MobileStatsPanel
              isOpen={showMobileStatsPanel}
              onClose={() => setShowMobileStatsPanel(false)}
              unitName={soldierConfig.name}
              damageType="normal"
              armorType="medium"
              armorValue={selectedSoldier.armor}
              isDesktop={isDesktop}
              pillarWidth={pillarWidth}
            />
          )
        }
        if (showingSummon && selectedSummon) {
          return (
            <MobileStatsPanel
              isOpen={showMobileStatsPanel}
              onClose={() => setShowMobileStatsPanel(false)}
              unitName={selectedSummon.name || selectedSummonConfig?.displayName || "Summon"}
              damageType={selectedSummon.damageType}
              armorType={selectedSummon.armorType}
              armorValue={selectedSummon.armor}
              isDesktop={isDesktop}
              pillarWidth={pillarWidth}
            />
          )
        }
        if (showingEnemy && selectedEnemy) {
          return (
            <MobileStatsPanel
              isOpen={showMobileStatsPanel}
              onClose={() => setShowMobileStatsPanel(false)}
              unitName={selectedEnemyConfig?.displayName || "Enemy"}
              damageType={selectedEnemy.damageType}
              armorType={selectedEnemy.defenseType}
              armorValue={selectedEnemy.armor}
              isDesktop={isDesktop}
              pillarWidth={pillarWidth}
            />
          )
        }
        if (hero && !isDroppodSelected) {
          return (
            <MobileStatsPanel
              isOpen={showMobileStatsPanel}
              onClose={() => setShowMobileStatsPanel(false)}
              unitName={heroConfig?.displayName || heroConfig?.name || "Hero"}
              damageType={hero.damageType}
              armorType={hero.armorType}
              armorValue={heroArmor}
              isDesktop={isDesktop}
              pillarWidth={pillarWidth}
            />
          )
        }
        return null
      })()}

      {/* Mobile Skills Panel - shows larger skill buttons for easier tapping */}
      {hero && !showingSummon && !showingEnemy && !showingSoldier && !showingTower && (
        <MobileSkillsPanel
          isOpen={showMobileSkillsPanel}
          onClose={() => setShowMobileSkillsPanel(false)}
          hero={hero}
          onUseSkill={onUseSkill}
          onMobileSkillActivated={onMobileSkillActivated}
          pendingSkillId={pendingSkillId}
        />
      )}

      {/* Mobile Inventory Panel - shows larger item slots for easier tapping */}
      {hero && heroInventory && !showingSummon && !showingEnemy && !showingSoldier && !showingTower && (
        <MobileInventoryPanel
          isOpen={showMobileInventoryPanel}
          onClose={() => setShowMobileInventoryPanel(false)}
          inventory={heroInventory}
          onUseItem={(slotIndex) => {
            if (onUseItem) {
              onUseItem(slotIndex)
            }
          }}
          onDropItem={(slotIndex) => {
            if (onDropItem) {
              onDropItem(slotIndex)
            }
          }}
          pendingItemSlot={pendingItemSlot}
        />
      )}

      {/* Transient top-centre notice: clone purchases (info) and failed
          crystal spends (error) */}
      {cloneToast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 rounded-lg px-4 py-2 shadow-lg z-50 animate-in fade-in slide-in-from-top-2 duration-200 whitespace-pre-line text-center ${
            toastTone === "error"
              ? "bg-red-950/95 border border-red-500/50 shadow-red-500/20"
              : "bg-blue-900/95 border border-blue-500/50 shadow-blue-500/20"
          }`}
        >
          <span
            className={`font-bold text-sm ${
              toastTone === "error" ? "text-red-200" : "text-blue-200"
            }`}
          >
            {cloneToast}
          </span>
        </div>
      )}
    </div>
  )
}
